// src/redux/procListLayoutSlice.ts
// 加工実績一覧レイアウト
import { createSlice } from "@reduxjs/toolkit";
import apiAxios from "../utils/apiAxios";
import { AnyAction, Dispatch } from "redux";
import { appendAlert } from "../redux/alertSlice";
import { GetPfmLayout } from "../models/GetPfmLayout";
import { DefaultLayouts } from "../utils/defaultLayout";
import cloneDeep from "lodash/cloneDeep";

// 先頭列は必ずチェックボックスを付与する
const CHECK_BOX_DEF = {
  headerName: "",
  field: "chkrow",
  checkboxSelection: true,
  headerCheckboxSelection: true,
  width: 50,
  comparator: "checkboxComparator",
  sortable: true,
};

const SORT_DEF = {
  headerName: "",
  field: "sort_order",
  width: 50,
  hide: true,
  sortable: true,
};

export type PfmLayout = {
  id: string;
  plant_code: string;
  proc_code: string;
  mach_code: string;
  layout_id: number;
  layout_name: string;
  columns: any[];
};

type procListLayouts = {
  pfmLayouts: PfmLayout[]; // APIで取得したレイアウトすべて
  filteredLayouts: PfmLayout[]; // フィルタしたレイアウト(加工・工場を選択後に画面で選択できる分)
  selectLayout: PfmLayout | null; // 選択したレイアウト
};

// 初期値
const initialState: procListLayouts = {
  pfmLayouts: [],
  filteredLayouts: [],
  selectLayout: null, // nullを許容するか、デフォルトをハードコードするか検討
};

export const procListLayoutSlice = createSlice({
  name: "procListLayoutSlice",
  initialState,
  reducers: {
    setProcListLayout: (state, { payload }) => {
      state.pfmLayouts = payload;
    },
    setFilterdLayout: (state, { payload }) => {
      state.filteredLayouts = payload;
      if (state.filteredLayouts.length > 0) {
        state.selectLayout = cloneDeep(state.filteredLayouts[0]);
      } else {
        state.selectLayout = cloneDeep(state.pfmLayouts[0]);
      }
    },
    setSelectLayout: (state, { payload }) => {
      // 工場・加工・機械・レイアウトIDを指定してフィルタ
      const layouts = state.filteredLayouts.filter((d) => d.id === payload.id);
      if (layouts.length > 0) {
        state.selectLayout = layouts[0];
      }
    },
  },
});

const { setProcListLayout, setFilterdLayout, setSelectLayout } =
  procListLayoutSlice.actions;

// 作業実績レイアウト定義取得
/*
レイアウト選択仕様
  1.コードに記述したレイアウト配列の後にAPI pfm_layoutsで取得したレイアウト配列を結合
    工場＋加工＋機械＋layout_idで全体で一意になるidを生成する
  2.idで重複を削除（重複が存在する場合　後が優先して残る）
  3.idで昇順ソート
  4.3で作成したレイアウトから工場・加工・機械（複数あり）に該当するレイアウトのみ抽出
  5.4で抽出したレイアウトをidで昇順ソート（これが画面で選択可能なレイアウトとなる）
  6.4が1件以上存在する場合は先頭を選択済みとする
    4が0件の場合は3のレイアウトの先頭を選択済みレイアウトとする（画面では選択ボタンはボタンは0件のためレイアウト選択はできない）

*/
const fetchProcListLayout = (
  plant_code: string = "",
  proc_code: string = "",
  mach_code: string[] = []
) => {
  return async (dispatch: Dispatch<AnyAction>) => {
    try {
      let pfmLayouts: PfmLayout[] = [];
      // APIからレイアウト情報取得
      const response = await apiAxios.get<GetPfmLayout[]>(`/pfm_layouts`);
      const data = response.data;
      const layout = data.map((d: GetPfmLayout) => {
        d.columns.unshift(SORT_DEF);
        d.columns.unshift(CHECK_BOX_DEF);
        return d;
      });
      // コードに定義されたレイアウト情報　reduxのstateはコードを保持できないので、colmnsはのぞいてレイアウト適用時に入れ直す
      const defLayout = DefaultLayouts.map((d: GetPfmLayout): GetPfmLayout => {
        return {
          plant_code: d.plant_code,
          proc_code: d.proc_code,
          mach_code: d.mach_code,
          layout_id: d.layout_id,
          layout_name: d.layout_name,
          columns: [],
        };
      });
      // APIで取得したレイアウトとコードで定義したレイアウトを結合
      // 同時に工場コード＋加工コード＋機械コード＋レイアウトIDで全体で一意となるキーを生成する
      pfmLayouts = [...defLayout, ...layout].map((d) => {
        return {
          id:
            d.plant_code +
            "-" +
            d.proc_code +
            "-" +
            d.mach_code +
            "-" +
            d.layout_id.toString(),
          ...d,
        };
      });

      // idで重複を削除
      let pfmLayoutsMap = new Map(pfmLayouts.map((l) => [l.id, l]));
      pfmLayouts = Array.from(pfmLayoutsMap.values());
      // idでソート
      pfmLayouts.sort((a: PfmLayout, b: PfmLayout) => {
        if (a.id > b.id) {
          return 1;
        } else {
          return -1;
        }
      });
      // 結合したレイアウトをStateへ保存
      dispatch(setProcListLayout(pfmLayouts));

      console.log(pfmLayouts);
      // 画面で選択した工場・加工・機械でフィルタ
      // 工場は複数選択されている分すべてを結合する
      let filterdLayouts: PfmLayout[] = [];
      mach_code.forEach((mach) => {
        const filterd = pfmLayouts.filter(
          (d: any) =>
            d.plant_code === plant_code &&
            d.proc_code === proc_code &&
            d.mach_code === mach
        );
        filterdLayouts = [...filterd, ...filterdLayouts];
      });
      // idでソート
      filterdLayouts.sort((a: PfmLayout, b: PfmLayout) => {
        if (a.id > b.id) {
          return 1;
        } else {
          return -1;
        }
      });
      console.log(filterdLayouts);
      // フィルタしたレイアウトをStateへ保存
      dispatch(setFilterdLayout(filterdLayouts));

      // フィルタしたレイアウトが存在しない場合はフィルタ前の先頭のレイアウトを適用
      // 存在すればフィルタ後の先頭レイアウトを適用
      if (filterdLayouts.length === 0) {
        dispatch(setSelectLayout(pfmLayouts[0].id));
      } else {
        dispatch(setSelectLayout(filterdLayouts[0].id));
      }
    } catch (error: any) {
      console.error(error);
      dispatch(
        appendAlert({
          severity: "error",
          message: "データの取得に失敗しました。" + error.message,
          displayTime: 5000,
        })
      );
    }
  };
};

export {
  fetchProcListLayout,
  setProcListLayout,
  setFilterdLayout,
  setSelectLayout,
};
export default procListLayoutSlice.reducer;
